/* eslint-disable react/no-array-index-key */
import React, { PropsWithChildren, useCallback, useState } from 'react';

import {
    Image,
    Linking,
    Modal,
    SafeAreaView,
    ScrollView,
    View,
} from 'react-native';

import { useTranslation } from 'react-i18next';

import { StackNavigationProp } from '@react-navigation/stack';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import useBackHandler from 'utils/hooks/useBackHandler.ts';
import { isPhone, isTabletOrWeb } from 'utils/responsiveness';

import { SUBSCRIBE_OVERLAY_SELFCARE_LINK } from 'screens/Detail/languageConst';
import {
    SETTINGS_STREAMING_SUBSCRIPTIONS_OVERLAY_DESCRIPTION_RN,
    SETTINGS_STREAMING_SUBSCRIPTIONS_OVERLAY_TITLE,
} from 'screens/Settings/languagesConst';

import { DialogButtons } from 'components/Dialog/components/DialogBody/components/DialogButtons';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import useLogos from './hooks/useLogos';
import styles from './styles';

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.SETTINGS_SUBSCRIPTIONS
    >;
};

const {
    ACCESSIBILITY: {
        SETTINGS: {
            MY_STREAMIN_SUBSCRIPTIONS: {
                ACCESSIBILITY_SELF_CARE_APP_BUTTON_SUBSCRIPTIONS,
            },
        },
    },
} = LANGUAGE_KEYS;

function Subscriptions({ navigation }: Props) {
    const { t } = useTranslation();

    const logos = useLogos();

    const [visible, setVisible] = useState(true);

    const handleOpenSelfcareApp = useCallback(() => {
        Linking.openURL(t(SUBSCRIBE_OVERLAY_SELFCARE_LINK));
    }, [t]);

    const handleOnClosePress = useCallback(() => {
        navigation.goBack();
        setVisible(false);

        return false;
    }, [navigation]);

    useBackHandler(handleOnClosePress);

    const Container = ({ children }: PropsWithChildren<{}>) =>
        isTabletOrWeb ? (
            <>{children}</>
        ) : (
            <View style={styles.phoneContainer}>{children}</View>
        );

    return (
        <Modal
            onRequestClose={handleOnClosePress}
            visible={visible}
            statusBarTranslucent
            transparent
        >
            <SafeAreaView
                style={[styles.safeArea, isTabletOrWeb && styles.webSafeArea]}
            >
                <Container>
                    <Text
                        style={styles.title}
                        category={ETextCategory.title1}
                        color={ETextColor.neutral}
                    >
                        {t(SETTINGS_STREAMING_SUBSCRIPTIONS_OVERLAY_TITLE)}
                    </Text>
                    <Text
                        style={styles.description}
                        category={ETextCategory.body2}
                    >
                        {t(
                            SETTINGS_STREAMING_SUBSCRIPTIONS_OVERLAY_DESCRIPTION_RN,
                        )}
                    </Text>
                    <ScrollView
                        accessible={false}
                        style={styles.stripe}
                        contentContainerStyle={styles.contentContainerStyle}
                        showsHorizontalScrollIndicator={false}
                        scrollEnabled={isPhone}
                        bounces={false}
                        overScrollMode="never"
                        horizontal
                    >
                        {logos.map(({ logo, label }, idx) => (
                            <Image
                                accessible
                                accessibilityLabel={label}
                                accessibilityRole="image"
                                key={idx}
                                source={logo}
                                style={[
                                    styles.logo,
                                    idx === logos.length - 1
                                        ? undefined
                                        : styles.logoIndent,
                                ]}
                            />
                        ))}
                    </ScrollView>
                </Container>
                <View style={styles.buttonsContainer}>
                    <DialogButtons
                        actionButtonTitle={LANGUAGE_KEYS.SELFCARE_APP_BUTTON}
                        actionButtonAccessibilityLabel={t(
                            ACCESSIBILITY_SELF_CARE_APP_BUTTON_SUBSCRIPTIONS,
                        )}
                        handleActionButtonPress={handleOpenSelfcareApp}
                        actionButtonStyle={styles.button}
                        actionButtonTextStyle={styles.actionButtonText}
                        handleCancelPress={handleOnClosePress}
                        cancelButtonBordered
                        cancelButtonTitle={LANGUAGE_KEYS.CLOSE_BUTTON}
                    />
                </View>
            </SafeAreaView>
        </Modal>
    );
}

export default Subscriptions;
